/**
 * CommandBar — Cmd+K palette for the Marketing Hub.
 *
 * Mocked agent dispatch. Receives natural-language intent, classifies it
 * client-side against a fixed pattern list, streams a fake "plan" with a 600ms
 * step cadence, surfaces an approval card that links to the artifact's home
 * page (Content Studio, Calendar, Approvals, etc.).
 *
 * Real backend wiring lives behind /api/marketing/commands (Phase 1 backend
 * work — out of scope for this preliminary frontend mockup).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Command, ArrowRight, Loader2, CheckCircle2, Bot } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const SAMPLE_PROMPTS = [
  "Schedule three LinkedIn posts about Echo EHR's ambient charting next week",
  "Draft a follow-up email to 'Demo No-Show — Last 30 Days' offering a recorded walkthrough",
  "What's our AI citation share on 'best EHR for primary care' vs athenahealth?",
  "Turn last month's revQ AI billing case study into a blog + 3 social variants + email",
  "Audit the Enable website for missing JSON-LD on the four new product pages",
];

/** Pick the best agent for the utterance — very loose intent matcher, mocked. */
function classify(utterance) {
  const u = utterance.toLowerCase();
  if (/(schedule|post|linkedin|instagram|twitter|x.com|facebook|tiktok)/.test(u))
    return { agent: "Social Agent", route: "/marketing/calendar", artifact: "3 scheduled posts" };
  if (/(email|follow.?up|nurture|subject line|send to)/.test(u))
    return { agent: "Email Agent", route: "/marketing/approvals", artifact: "Email draft" };
  if (/(citation|chatgpt|perplexity|share of answer|geo|schema|llms\.txt)/.test(u))
    return { agent: "SEO / GEO Agent", route: "/marketing/geo-seo", artifact: "Citation analysis" };
  if (/(blog|case study|article|long.?form|story|carousel|variant)/.test(u))
    return { agent: "Content Agent", route: "/marketing/content-studio", artifact: "Multi-channel content brief" };
  if (/(audience|segment|primary care|cardiology|specialty|practice)/.test(u))
    return { agent: "Audience Agent", route: "/marketing/audience", artifact: "Refined segment" };
  if (/(analyz|report|why|performance|metric|kpi)/.test(u))
    return { agent: "Analyst Agent", route: "/marketing/reports", artifact: "Performance narrative" };
  return { agent: "Content Agent", route: "/marketing/content-studio", artifact: "Draft brief" };
}

const STEP_TEMPLATES = {
  "Content Agent": [
    "Reading brand voice + recent content library…",
    "Selecting Design System template (Stat / Proof @ Square)…",
    "Filling named fields with on-brand copy…",
    "Generating 5 channel variants (LinkedIn / IG / Email / Blog / X)…",
    "Brand Compliance check — passed",
  ],
  "Social Agent": [
    "Resolving audience peak-engagement window…",
    "Picking template variant per platform (Square / Story / Wide)…",
    "Drafting channel-native copy…",
    "Queueing 3 scheduled posts (Ayrshare — MOCKED)…",
    "Brand Compliance check — passed",
  ],
  "Email Agent": [
    "Resolving recipient list from segment…",
    "Filtering against suppression list (MOCKED)…",
    "Drafting subject + preview + body…",
    "Render Design System email template (Helvetica fallback stack)…",
    "Send-time optimization · queued for Friday 10:00am ET",
  ],
  "SEO / GEO Agent": [
    "Querying ChatGPT / Claude / Perplexity / Google AI on tracked terms…",
    "Computing share-of-answer vs competitors…",
    "Auditing JSON-LD coverage on Enable website…",
    "Generating proposed schema diffs (4 pages)…",
    "Ready for review",
  ],
  "Audience Agent": [
    "Parsing segment criteria…",
    "Resolving CRM contacts (de-identified, PHI-firewalled)…",
    "Live count: 3,847 contacts match",
    "Proposing look-alike expansion (+812)…",
    "Saved as draft segment",
  ],
  "Analyst Agent": [
    "Pulling channel metrics last 30 days…",
    "Cross-referencing pipeline data…",
    "Detecting anomalies…",
    "Writing narrative summary…",
    "Ready",
  ],
};

export default function CommandBar({ open, onClose }) {
  const [utterance, setUtterance] = useState("");
  const [phase, setPhase] = useState("input"); // input | planning | approve | done
  const [steps, setSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [plan, setPlan] = useState(null);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setUtterance("");
      setPhase("input");
      setSteps([]);
      setActiveStep(0);
      setPlan(null);
    } else {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Stream steps
  useEffect(() => {
    if (phase !== "planning") return;
    if (activeStep >= steps.length) {
      setPhase("approve");
      return;
    }
    const t = setTimeout(() => setActiveStep((s) => s + 1), 700);
    return () => clearTimeout(t);
  }, [phase, activeStep, steps.length]);

  const submit = (text) => {
    const u = (text || utterance).trim();
    if (!u) return;
    const dispatch = classify(u);
    const stepList = STEP_TEMPLATES[dispatch.agent] || STEP_TEMPLATES["Content Agent"];
    setPlan(dispatch);
    setSteps(stepList);
    setActiveStep(0);
    setPhase("planning");
  };

  const approveAndGo = () => {
    setPhase("done");
    toast.success(`${plan.agent}: ${plan.artifact} ready — opening canvas`);
    setTimeout(() => {
      onClose?.();
      navigate(plan.route);
    }, 400);
  };

  const suggestions = useMemo(() => SAMPLE_PROMPTS.slice(0, 4), []);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="cmd-dialog" data-testid="command-bar-dialog">
        <DialogTitle style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
          Marketing Command Bar
        </DialogTitle>
        <DialogDescription style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
          Natural-language command palette for the Marketing Hub. Type an intent and the agent will plan and execute it.
        </DialogDescription>
        <div className="cmd-shell">
          <div className="cmd-header">
            <Command style={{ width: 14, height: 14, color: "var(--blue)" }} />
            <span className="cmd-eyebrow">Marketing Command Bar</span>
          </div>

          {phase === "input" && (
            <>
              <div className="cmd-input-row">
                <Sparkles style={{ width: 16, height: 16, color: "var(--blue)" }} />
                <input
                  ref={inputRef}
                  className="cmd-input"
                  placeholder="What do you want to do?"
                  value={utterance}
                  onChange={(e) => setUtterance(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  data-testid="command-bar-input"
                />
                <button onClick={() => submit()} className="btn btn-primary" data-testid="command-bar-submit">
                  Run <ArrowRight style={{ width: 12, height: 12 }} />
                </button>
              </div>
              <div className="cmd-suggestions">
                <div className="eyebrow">Try</div>
                {suggestions.map((s) => (
                  <button key={s} onClick={() => submit(s)} className="cmd-suggestion" data-testid={`cmd-sample-${s.length}`}>
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}

          {(phase === "planning" || phase === "approve") && plan && (
            <div className="cmd-plan">
              <div className="cmd-plan-header">
                <Bot style={{ width: 13, height: 13 }} />
                <strong>{plan.agent}</strong>
                <span className="cmd-plan-utterance">{utterance}</span>
              </div>
              <ol className="cmd-steps">
                {steps.map((s, i) => (
                  <li key={i} className={`cmd-step ${i < activeStep ? "is-done" : i === activeStep ? "is-active" : ""}`}>
                    {i < activeStep ? (
                      <CheckCircle2 style={{ width: 13, height: 13, color: "var(--success)" }} />
                    ) : i === activeStep ? (
                      <Loader2 style={{ width: 13, height: 13, animation: "spin 1s linear infinite", color: "var(--blue)" }} />
                    ) : (
                      <span className="cmd-step-dot" />
                    )}
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
              {phase === "approve" && (
                <div className="cmd-approve">
                  <div className="cmd-artifact">
                    Artifact ready: <strong>{plan.artifact}</strong>
                  </div>
                  <button onClick={approveAndGo} className="btn btn-primary" data-testid="command-bar-approve">
                    Approve &amp; open <ArrowRight style={{ width: 12, height: 12 }} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Hook — global ⌘K / Ctrl+K listener. */
export function useCommandBar() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  return { open, setOpen };
}
